/**
 * Boost PLP: when a variant swatch is selected, update card price and PDP links
 * (`?variant=` from `data-variant-id` on the swatch input).
 *
 * Uses delegated listeners so it works when Boost injects the grid after load.
 *
 * Boost / browser quirks: `change` alone can miss some second clicks (e.g. label
 * repaints or competing handlers). We also sync on `click` (capture) + rAF once
 * the radio's checked state has settled.
 */
(function () {
  'use strict';

  if (window.__boostPlpVariantPricesInit) return;
  window.__boostPlpVariantPricesInit = true;

  const cfg = window.boostPlpVariantPricesConfig || {};
  const currencyCode = cfg.currencyCode || window.Shopify?.currency?.active || 'USD';

  let coalesceRaf = null;
  let coalesceInput = null;

  function parseAmount(raw) {
    if (raw === undefined || raw === null || raw === '') return NaN;
    const n = parseFloat(String(raw).replace(/[^\d.-]/g, ''), 10);
    return Number.isFinite(n) ? n : NaN;
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function isOnSale(priceNum, compareNum) {
    if (Number.isNaN(priceNum)) return false;
    if (Number.isNaN(compareNum)) return false;
    return compareNum > priceNum;
  }

  function getCardRoot(input) {
    return input.closest('.boost-sd__product-item');
  }

  function setPriceMarkup(priceRoot, onSale, formattedPrice, formattedCompare) {
    const content = priceRoot.querySelector('.boost-sd__product-price-content');
    if (!content) return;

    if (onSale) {
      content.innerHTML =
        '<span class="boost-sd__product-price--sale"><span class="boost-sd__format-currency">' +
        formattedPrice +
        '</span></span><span class="boost-sd__format-currency boost-sd__format-currency--price-compare boost-sd__product-price--compare"><span class="boost-sd__format-currency">' +
        formattedCompare +
        '</span></span>';
    } else {
      content.innerHTML =
        '<span class="boost-sd__product-price--default"><span class="boost-sd__format-currency">' +
        formattedPrice +
        '</span></span>';
    }
  }

  function setVisuallyHidden(priceRoot, onSale, formattedPrice, formattedCompare) {
    const el = priceRoot.querySelector('.boost-sd__visually-hidden');
    if (!el) return;
    if (onSale) {
      el.textContent = 'Regular price ' + formattedCompare + ', now on sale for ' + formattedPrice;
    } else {
      el.textContent = 'Regular price ' + formattedPrice;
    }
  }

  function syncAriaChecked(input) {
    const group = input.closest('.boost-sd__product-swatch-options');
    if (!group) return;
    group.querySelectorAll('input.boost-sd__radio-input[type="radio"]').forEach((radio) => {
      radio.setAttribute('aria-checked', radio === input && radio.checked ? 'true' : 'false');
    });
  }

  /**
   * Point product card links at the selected variant PDP (`/products/handle?variant=id`).
   */
  function updateProductCardPdpLinks(card, variantIdRaw) {
    const links = card.querySelectorAll('a.boost-sd__product-link[href]');
    if (!links.length) return;

    const id = variantIdRaw != null ? String(variantIdRaw).trim() : '';

    links.forEach((a) => {
      const href = a.getAttribute('href');
      if (!href || href.indexOf('/products/') === -1) return;

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (id) {
        url.searchParams.set('variant', id);
      } else {
        url.searchParams.delete('variant');
      }

      const next = url.pathname + (url.search || '') + (url.hash || '');
      a.setAttribute('href', next);
    });
  }

  function isSwatchPriceInput(el) {
    return (
      el instanceof HTMLInputElement &&
      el.type === 'radio' &&
      el.classList.contains('boost-sd__radio-input') &&
      el.hasAttribute('data-variant-price')
    );
  }

  function applyPriceForInput(input) {
    if (!isSwatchPriceInput(input) || !input.checked) return;

    const card = getCardRoot(input);
    if (!card) return;

    const priceRoot = card.querySelector('.boost-sd__product-price');
    if (!priceRoot) return;

    const priceNum = parseAmount(input.dataset.variantPrice);
    if (Number.isNaN(priceNum)) return;

    const compareNum = parseAmount(input.dataset.compareAtPrice);
    const onSale = isOnSale(priceNum, compareNum);

    const formattedPrice = formatMoney(priceNum);
    const formattedCompare = onSale ? formatMoney(compareNum) : '';

    setPriceMarkup(priceRoot, onSale, formattedPrice, formattedCompare);
    setVisuallyHidden(priceRoot, onSale, formattedPrice, formattedCompare);
    syncAriaChecked(input);
    updateProductCardPdpLinks(card, input.dataset.variantId);
  }

  function scheduleApplyPrice(input) {
    if (!isSwatchPriceInput(input)) return;
    coalesceInput = input;
    if (coalesceRaf != null) return;
    coalesceRaf = requestAnimationFrame(() => {
      coalesceRaf = null;
      const pending = coalesceInput;
      coalesceInput = null;
      if (!pending) return;
      /** When switching swatches, the *unchecked* radio may fire `change` last; always use the group's checked input. */
      let target = pending.checked ? pending : null;
      if (!target) {
        const group = pending.closest('.boost-sd__product-swatch-options');
        target = group?.querySelector('input.boost-sd__radio-input[data-variant-price]:checked') || null;
      }
      if (target) applyPriceForInput(target);
    });
  }

  function onSwatchChange(event) {
    const input = event.target;
    if (!isSwatchPriceInput(input)) return;
    scheduleApplyPrice(input);
  }

  /**
   * Resolve swatch input from a click (label, label text, or input).
   * Prefer the radio inside the clicked swatch option, then fall back to group checked.
   */
  function onSwatchPointerClick(event) {
    const option = event.target?.closest?.('.boost-sd__product-swatch-option');
    if (!option) return;

    const group = option.closest('.boost-sd__product-swatch-options');
    if (!group) return;

    const inputInOption = option.querySelector('input.boost-sd__radio-input[data-variant-price]');
    if (inputInOption) {
      scheduleApplyPrice(inputInOption);
      return;
    }

    const checked = group.querySelector('input.boost-sd__radio-input[data-variant-price]:checked');
    if (checked) scheduleApplyPrice(checked);
  }

  document.addEventListener('change', onSwatchChange);
  document.addEventListener('click', onSwatchPointerClick, true);
})();
